import { Component, State, h, Element } from '@stencil/core';
import { BreadcrumbItem } from './interfaces';
import KeyBoardArrowLeftIcon from "../../assets/svg/keyboard_arrow_left.svg"
import KeyBoardArrowRightIcon from "../../assets/svg/keyboard_arrow_right.svg"

/**
 * @name SallaBreadcrumb
 * @description A StencilJS component for rendering breadcrumb navigation.
 * @tag salla-breadcrumb
 *
 * @slot item - Replaces breadcrumb item, has replaceable props `{url}`, `{title}`.
 * @slot icon - Replaces breadcrumb arrow icon.
 */
@Component({
  tag: 'salla-breadcrumb',
  styleUrl: 'salla-breadcrumb.scss',
})
export class SallaBreadcrumb {
  constructor() {

    // Initialize slot HTML or use default if not provided
    this.itemSlot = this.host.querySelector('[slot="item"]')?.outerHTML || `<li class="s-breadcrumb-item"><a href={url}>{title}</a></li>`;
    this.iconSlot = this.host.querySelector('[slot="icon"]')?.outerHTML
  }

  @Element() host: HTMLElement;

  // /**
  //  * Breadcrumb items in array
  //  */
  // @Prop() items: string;

  private readonly itemSlot: string;
  private readonly iconSlot: string;

  @State() breadcrumbs: BreadcrumbItem[];
  componentWillLoad() {
    return (new Promise(resolve => salla.onReady(() => salla.lang.onLoaded(resolve))))
      .then(() => {
        if (salla.url.is_page('index')) {
          throw new Error('salla-breadcrumb:: breadcrumb not supported on home page');
        }
      })
      .then(() => {
        if (!salla.config.get('theme.settings.is_breadcrumbs_enabled', true)) {
          throw new Error('salla-breadcrumb:: merchant disabled the feature');
        }
      })
      .then(() => {
        const page = salla.config.get("page");
        if (!page || !page.slug) {
          salla.logger.error('salla-breadcrumbs:: page object not existed on salla.config.get("page")!');
          return [];
        }

        //get from api on these pages
        if (['product.single', 'product.index'].includes(page.slug)) {
          return salla.api.navigation.fetchBreadcrumbs({ page: page.slug, id: page.id, referrer: document.referrer })
            .then(resp => this.breadcrumbs = resp.data);
        }

        //otherwise generate the breadcrumb

        this.breadcrumbs = [{ title: salla.lang.get('common.titles.home'), url: salla.url.get('') }];

        //inject my_account on the breadcrumb
        if (page.slug.includes("customer")) {
          this.breadcrumbs.push({ title: salla.lang.get('common.titles.profile'), url: salla.url.get('profile') });
        }

        //inject blog on the breadcrumb
        if (page.slug.includes("blog")) {
          this.breadcrumbs.push({ title: salla.lang.get('blocks.footer.blog'), url: salla.url.get('blog') });
        }

        //inject brands on the breadcrumb
        if (page.slug == 'brands.single') {
          this.breadcrumbs.push({ title: salla.lang.get('common.titles.brands'), url: salla.url.get('brands') });
        }

        //inject loyalty on the breadcrumb, for some reason salla.config.get('page.title') not passed!!
        if (!page.title && page.slug == "loyalty") {
          this.breadcrumbs.push({ title: salla.lang.get('common.titles.loyalty_program'), url: salla.url.get('loyalty') });
        }

        //add the current page title as the last breadcrumb item, only if it's not set before
        if (page.title && !['customer.profile', 'brands.index', 'blog.index'].includes(page.slug)) {
          this.breadcrumbs.push({ title: page.title });
        }

        return this.breadcrumbs;
      })
      .then(() => this.breadcrumbs.length && (this.breadcrumbs[this.breadcrumbs.length - 1].is_last = true))
      .catch((error) => {
        salla.logger.error('salla-breadcrumb:: unexpected error!', error);
        this.breadcrumbs = [];
      });
  }

  /**
   * Renders the breadcrumb component.
   * @returns JSX for rendering breadcrumbs.
   */
  render() {
    if (this.breadcrumbs.length <= 1) {
      salla.log('salla-breadcrumb:: There is no breadcrumbs!');
      return;
    }
    return (
      <ol class={{
        "s-breadcrumb-wrapper": true,
        "s-breadcrumb-dark": salla.url.is_page('loyalty'),
        "s-breadcrumb-primary-reverse": salla.config.get('page.slug').includes('customer')
      }}>
        {this.breadcrumbs.map(item => [
          <div class="s-breadcrumb-slot" innerHTML={this.itemSlot.replace(/\{url\}/g, item.url).replace(/\{title\}/g, item.title)}></div>,
          this.getArrowDomForItem(item),
        ]
        )}
      </ol>
    );
  }

  private getArrowDomForItem(item: BreadcrumbItem) {
    if (item.is_last) {
      return '';
    }
    let iconDom = this.iconSlot || (salla.config.get('theme.is_rtl', true) ? KeyBoardArrowLeftIcon : KeyBoardArrowRightIcon);

    return <li class="s-breadcrumb-arrow">
      <div class={{ "s-breadcrumb-icon-slot": true, "s-breadcrumb-default-icon": !this.iconSlot }} innerHTML={iconDom}></div>
    </li>;
  }

  /**
   * Lifecycle method called after the component is rendered.
   * - Reduces the number of elements in the DOM.
   * - Removes unnecessary slots parent elements.
   * - Replaces the last anchor tag in the breadcrumb with its content.
   */
  componentDidRender() {
    // Reduces the number of elements in the DOM
    this.host.querySelectorAll('.s-breadcrumb-slot').forEach(el => el.replaceWith(el.firstChild));
    this.host.querySelectorAll('.s-breadcrumb-icon-slot.s-breadcrumb-default-icon').forEach(el => el.replaceWith(el.querySelector('svg')));

    // Removes the slots parent elements if exists
    this.host.querySelector('[slot="item"]')?.remove();
    this.host.querySelector('[slot="icon"]')?.remove();

    // Removes the last anchor tag in the breadcrumb
    let lastEl = this.host.querySelectorAll('.s-breadcrumb-item')[this.breadcrumbs.length - 1]?.querySelector('a');
    lastEl && lastEl.replaceWith(lastEl.firstChild);
  }
}
